import React, { useState } from 'react';
import { TransformGroup } from './groups/TransformGroup';
import { TextGroup } from './groups/TextGroup';
import { BackgroundGroup } from './groups/BackgroundGroup';
import { BorderGroup } from './groups/BorderGroup';
import { EffectsGroup } from './groups/EffectsGroup';
import { SpacingGroup } from './groups/SpacingGroup';
import { File, Package, ChevronDown, Box, Type, Square, Image as ImageIcon, TextCursor, Link, List, ArrowDownRight } from 'lucide-react';
import { EditModePreviewHandle } from '../EditModePreview';

interface TransformData {
  x: number;
  y: number;
  scaleX: number;
  scaleY: number;
  rotation: number;
}

interface TextData {
  content: string;
  fontSize: number;
  fontFamily: string;
  fontWeight: string;
  color: string;
  textAlign: string;
}

interface BackgroundLayer {
  id: string;
  type: 'linear-gradient' | 'radial-gradient' | 'image';
  value: string;
}

interface BackgroundData {
  backgroundColor: string;
  backgroundImage: string;
  backgroundSize: string;
  backgroundPosition: string;
  backgroundRepeat: string;
  layers?: BackgroundLayer[];
}

interface BorderData {
  borderWidth: number;
  borderColor: string;
  borderStyle: string;
  borderRadius: number;
}

interface EffectsData {
  opacity: number;
}

interface SpacingData {
  paddingTop: number;
  paddingRight: number;
  paddingBottom: number;
  paddingLeft: number;
  marginTop: number;
  marginRight: number;
  marginBottom: number;
  marginLeft: number;
}

interface SelectedElementData {
  editId: string | null;
  tagName: string;
  compName?: string | null;
  transform: TransformData;
  text?: TextData;
  background?: BackgroundData;
  border?: BorderData;
  effects?: EffectsData;
  spacing?: SpacingData;
}

interface Section {
  name: string;
  editId: string;
  isActive?: boolean;
}

interface Template {
  name: string;
  editId: string;
}

interface PropertiesPanelProps {
  selectedElement: SelectedElementData | null;
  sections?: Section[];
  templates?: Template[];
  editModePreviewRef?: React.RefObject<EditModePreviewHandle>;
  onTransformChange: (property: keyof TransformData, value: number) => void;
  onTextChange?: (property: keyof TextData, value: string | number) => void;
  onBackgroundChange?: (property: keyof BackgroundData | 'layers', value: string | BackgroundLayer[]) => void;
  onBorderChange?: (property: keyof BorderData, value: string | number) => void;
  onEffectsChange?: (property: keyof EffectsData, value: number) => void;
  onSpacingChange?: (property: keyof SpacingData, value: number) => void;
  onLockSizeChange?: (locked: boolean) => void;
}

export const PropertiesPanel: React.FC<PropertiesPanelProps> = ({
  selectedElement,
  sections = [],
  templates = [],
  editModePreviewRef,
  onTransformChange,
  onTextChange,
  onBackgroundChange,
  onBorderChange,
  onEffectsChange,
  onSpacingChange,
  onLockSizeChange
}) => {
  const hasSelection = selectedElement && selectedElement.editId;
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  
  // Find the currently active section, or default to first section if none are marked active
  const activeSection = sections.find(s => s.isActive) || (sections.length > 0 ? sections[0] : null);
  
  // Get icon based on tag type
  const getTagIcon = (tagName: string) => {
    const tag = tagName.toLowerCase();
    
    // Heading tags
    if (['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p', 'span', 'label'].includes(tag)) {
      return <Type className="w-4 h-4 text-purple-500" />;
    }
    
    // Button
    if (tag === 'button') {
      return <Square className="w-4 h-4 text-blue-500" />;
    }
    
    // Image
    if (tag === 'img') {
      return <ImageIcon className="w-4 h-4 text-green-500" />;
    }
    
    // Input/Textarea
    if (['input', 'textarea'].includes(tag)) {
      return <TextCursor className="w-4 h-4 text-orange-500" />;
    }
    
    // Link
    if (tag === 'a') {
      return <Link className="w-4 h-4 text-blue-600" />;
    }
    
    // List
    if (['ul', 'ol', 'li'].includes(tag)) {
      return <List className="w-4 h-4 text-gray-600" />;
    }
    
    // Container elements (div, section, article, main, etc.)
    return <Box className="w-4 h-4 text-gray-500" />;
  };
  
  // Handle section click to activate/show section
  const handleSectionClick = (section: Section) => {
    const iframe = editModePreviewRef?.current?.iframeRef.current?.contentWindow;
    if (iframe) {
      // First deactivate any active template
      iframe.postMessage({ type: 'deactivate-template' }, '*');
      // Then activate the section
      iframe.postMessage({
        type: 'activate-section',
        editId: section.editId
      }, '*');
    }
    setIsDropdownOpen(false);
  };
  
  // Handle template click to show template for editing
  const handleTemplateClick = (template: Template) => {
    const iframe = editModePreviewRef?.current?.iframeRef.current?.contentWindow;
    if (iframe) {
      iframe.postMessage({
        type: 'activate-template',
        editId: template.editId
      }, '*');
    }
    setIsDropdownOpen(false);
  };

  return (
    <div className="w-full h-full bg-gray-100 dark:bg-gray-800 flex flex-col">
      {/* Compact Header - Always visible */}
      <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
        <span className="text-xs font-semibold text-gray-900 dark:text-white uppercase tracking-wide">
          Properties
        </span>
      </div>

      {/* Sections & Templates Dropdown */}
      <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-700">
        <div className="relative">
          <button
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            className="w-full flex items-center justify-between px-3 py-2 text-sm bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
          >
            <span className="text-gray-700 dark:text-gray-300 flex items-center gap-2">
              {activeSection ? (
                <>
                  <File className="w-4 h-4 text-blue-500" />
                  {activeSection.name}
                </>
              ) : (
                'No active section'
              )}
            </span>
            <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
          </button>

          {/* Dropdown Menu */}
          {isDropdownOpen && (sections.length > 0 || templates.length > 0) && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded shadow-lg z-10 max-h-64 overflow-y-auto">
              {/* Sections */}
              {sections.length > 0 && (
                <>
                  <div className="px-3 py-1.5 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide bg-gray-50 dark:bg-gray-800">
                    Sections
                  </div>
              {sections.map((section) => (
                <button
                  key={section.editId}
                  onClick={() => handleSectionClick(section)}
                  className={`w-full flex items-center gap-2 px-3 py-2 text-sm ${
                    section.isActive 
                      ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300' 
                      : 'text-gray-700 dark:text-gray-300'
                  } hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-left`}
                >
                  <File className={`w-4 h-4 ${section.isActive ? 'text-blue-600' : 'text-blue-500'}`} />
                      <span>{section.name}</span>
                    </button>
                  ))}
                </>
              )}

              {/* Templates */}
              {templates.length > 0 && (
                <>
                  <div className="px-3 py-1.5 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide bg-gray-50 dark:bg-gray-800">
                    Templates
                  </div>
                  {templates.map((template) => (
                    <button
                      key={template.editId}
                      onClick={() => handleTemplateClick(template)}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-left"
                    >
                      <Package className="w-4 h-4 text-purple-500" />
                      <span>{template.name}</span>
                    </button>
                  ))}
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Selected Component Name */}
      {hasSelection && selectedElement.compName && (
        <div className="px-3 py-2.5 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
          <div className="flex items-center justify-between">
            <div className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              {getTagIcon(selectedElement.tagName)}
              {selectedElement.compName}
            </div>
            <button className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded transition-colors">
              <ArrowDownRight className="w-4 h-4 text-gray-600 dark:text-gray-400" />
            </button>
          </div>
        </div>
      )}

      {/* Content */}
      {!hasSelection ? (
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="text-center">
            <div className="text-gray-600 dark:text-gray-400 text-sm">No element selected</div>
            <div className="text-gray-500 dark:text-gray-500 text-xs mt-1">
              Click an element to view its properties
            </div>
          </div>
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto p-4 space-y-2">

          <TransformGroup
            transform={selectedElement.transform}
            onTransformChange={onTransformChange}
            onLockSizeChange={onLockSizeChange}
          />
          
          {selectedElement.background && onBackgroundChange && (
            <BackgroundGroup
              background={selectedElement.background}
              onBackgroundChange={onBackgroundChange}
            />
          )}
          
          {selectedElement.border && onBorderChange && (
            <BorderGroup
              border={selectedElement.border}
              onBorderChange={onBorderChange}
            />
          )}
          
          {selectedElement.effects && onEffectsChange && (
            <EffectsGroup
              effects={selectedElement.effects}
              onEffectsChange={onEffectsChange}
            />
          )}
          
          {selectedElement.spacing && onSpacingChange && (
            <SpacingGroup
              spacing={selectedElement.spacing}
              onSpacingChange={onSpacingChange}
            />
          )}
          
          {selectedElement.text && onTextChange && (
            <TextGroup
              text={selectedElement.text}
              onTextChange={onTextChange}
            />
          )}
        </div>
      )}
    </div>
  );
};

